/**
 * Superadmin → Platform insights. Tyashin's own product KPIs
 * (YOM-ANALYTICS-TRACKING-PLAN.md §A1).
 *
 *   1. Product KPIs — computed daily by the backend `platform-kpi-rollup` job
 *      from Tyashin's own data (WVS, activation, TTV, retention, WAU/MAU,
 *      adoption, sign-up to paid, AI cost, deploys, CWV). Independent of YOM:
 *      they render even while the YOM flags are off.
 *   2. Event reports — live `<yom-report>` charts from the T-APP (admin +
 *      backend) and T-SITES (aggregate customer-site outcomes) YOM projects.
 *
 * A KPI that cannot be computed says why. We never show a made-up number.
 */
import { DataState } from '@/components/common/DataState';
import { analyticsEmbedHelpers } from '@/lib/api';
import { useEmbedTokens } from '@/components/analytics/yom/useEmbedTokens';
import { YomReport } from '@/components/analytics/yom/YomReport';
import { ReportSkeleton } from '@/components/analytics/yom/ReportSkeleton';
import { RangeControls, useRangeState } from '@/components/analytics/yom/RangeControls';
import { PlatformKpiSections } from '@/components/analytics/platform/PlatformKpiSections';

type Src = 'app' | 'sites';

/** A group of live YOM reports, two across. */
function Section({
  title,
  description,
  children,
}: {
  title: string;
  description: string;
  children?: React.ReactNode;
}) {
  return (
    <section className="space-y-4">
      <div>
        <h3 className="text-base font-semibold">{title}</h3>
        <p className="text-muted-foreground text-sm">{description}</p>
      </div>
      {children && <div className="grid gap-5 lg:grid-cols-2">{children}</div>}
    </section>
  );
}

export default function PlatformInsightsPage() {
  const { state, update } = useRangeState('30d');
  const { range } = state;
  const { manager, first } = useEmbedTokens('platform-insights', () =>
    analyticsEmbedHelpers.getPlatformInsightsTokens()
  );

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Platform insights</h1>
        <p className="text-muted-foreground mt-1 text-sm">
          Tyashin&apos;s own numbers: whether customer sites are live and delivering, how builders
          use the product, and what it costs to run.
        </p>
      </div>

      <RangeControls state={state} update={update} showCompare={false} />

      <PlatformKpiSections range={range} />

      <div className="border-t pt-8">
        <h2 className="text-lg font-semibold">Event reports</h2>
        <p className="text-muted-foreground text-sm">
          Live charts from the T-APP and T-SITES YOM projects. They fill in once the YOM platform
          events are switched on.
        </p>
      </div>

      <DataState
        query={first}
        hideRefreshingHint
        skeleton={
          <div className="grid gap-5 lg:grid-cols-2">
            <ReportSkeleton variant="kpis" className="min-h-[200px]" />
            <ReportSkeleton variant="kpis" className="min-h-[200px]" />
          </div>
        }
      >
        {(bundle) => {
          if (!manager) return null;
          const has = (s: Src) => Boolean(bundle.sources[s]);
          const funnelId = (key: string) => (has('app') ? bundle.meta?.[key] : undefined);
          const activationId = funnelId('funnel:activation');
          const trialId = funnelId('funnel:trial_to_paid');
          return (
            <div className="space-y-10">
              {bundle.meta?.missing && (
                <div className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200">
                  Some reports are hidden because these settings are missing on the backend:{' '}
                  <code className="font-mono text-xs">{bundle.meta.missing}</code>
                </div>
              )}
              <Section
                title="Pulse"
                description="Headline activity for the selected dates, against the period before."
              >
                {has('sites') && (
                  <YomReport
                    manager={manager}
                    source="sites"
                    report="overview"
                    title="Outcomes on customer sites"
                    kpis={['leads', 'purchases', 'revenue']}
                    compare
                    currency="USD"
                    range={range}
                    skeleton="kpis"
                  />
                )}
                {has('app') && (
                  <YomReport
                    manager={manager}
                    source="app"
                    report="overview"
                    title="Admin usage"
                    kpis={['visitors', 'sessions', 'events']}
                    compare
                    range={range}
                    skeleton="kpis"
                  />
                )}
              </Section>

              <Section
                title="North star and activation"
                description="Weekly value-delivering sites (WVS), activation and time to value."
              >
                {has('sites') && (
                  <YomReport
                    manager={manager}
                    source="sites"
                    report="timeseries"
                    title="Leads delivered to customers, per week"
                    metric="leads"
                    interval="week"
                    range={range}
                    skeleton="chart"
                  />
                )}
                {has('sites') && (
                  <YomReport
                    manager={manager}
                    source="sites"
                    report="breakdown"
                    title="Outcomes by type"
                    dim="event"
                    limit={8}
                    range={range}
                    skeleton="table"
                  />
                )}
                {activationId && (
                  <YomReport
                    manager={manager}
                    source="app"
                    report="funnel"
                    funnel={activationId}
                    title="Activation: sign-up to a live site with visits"
                    range={range}
                    skeleton="funnel"
                  />
                )}
              </Section>

              <Section
                title="Retention and engagement"
                description="Do builders come back, and how often."
              >
                {has('app') && (
                  <YomReport
                    manager={manager}
                    source="app"
                    report="timeseries"
                    title="People using the admin each week (any activity)"
                    metric="visitors"
                    interval="week"
                    range={range}
                    skeleton="chart"
                  />
                )}
                {has('app') && (
                  <YomReport
                    manager={manager}
                    source="app"
                    report="breakdown"
                    title="Plugin installs by plugin"
                    dim="d:plugin_id"
                    event="plugin_installed"
                    limit={8}
                    range={range}
                    skeleton="table"
                  />
                )}
              </Section>

              <Section title="Revenue and cost" description="Trials, paid plans and AI spend.">
                {trialId && (
                  <YomReport
                    manager={manager}
                    source="app"
                    report="funnel"
                    funnel={trialId}
                    title="Trial to paid"
                    range={range}
                    skeleton="funnel"
                  />
                )}
                {has('app') && (
                  <YomReport
                    manager={manager}
                    source="app"
                    report="breakdown"
                    title="AI jobs by feature"
                    dim="d:feature"
                    event="ai_job_completed"
                    limit={8}
                    range={range}
                    skeleton="table"
                  />
                )}
              </Section>
            </div>
          );
        }}
      </DataState>
      <p className="text-muted-foreground text-center text-xs">Analytics by YOM</p>
    </div>
  );
}
